import React, { useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { S, A, DARK, labelStyle } from './tokens';

const useStyles = createUseStyles({
  '@keyframes gxShimmer': { '0%': { backgroundPosition: '-200% 0' }, '100%': { backgroundPosition: '200% 0' } },
  frame: {
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 12,
    '@media (max-width: 900px)': { overflowX: 'auto' }
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontFamily: S.ui,
    fontSize: 14,
    color: S.fg,
    '& th': {
      ...labelStyle,
      position: 'sticky',
      top: 0,
      zIndex: 1,
      height: 38,
      padding: '0 12px',
      textAlign: 'left',
      whiteSpace: 'nowrap',
      background: S.chrome,
      borderBottom: `1px solid ${S.line}`,
      '&:first-child': { borderTopLeftRadius: 12 },
      '&:last-child': { borderTopRightRadius: 12 }
    },
    '& td': {
      padding: '10px 12px',
      borderBottom: `1px solid ${A.rowLine}`,
      verticalAlign: 'middle'
    },
    '& tbody tr:last-child td': { borderBottom: 0 },
    '& tbody tr:last-child td:first-child': { borderBottomLeftRadius: 12 },
    '& tbody tr:last-child td:last-child': { borderBottomRightRadius: 12 }
  },
  clickable: {
    cursor: 'pointer',
    '&:hover td': { background: A.rowHover }
  },
  selected: { '& td': { background: `${A.rowSelected} !important` } },
  bar: {
    height: 12,
    borderRadius: 6,
    background: `linear-gradient(90deg, ${S.chromeHover} 0%, ${S.chrome} 50%, ${S.chromeHover} 100%)`,
    backgroundSize: '200% 100%',
    animation: '$gxShimmer 1.4s ease-in-out infinite'
  },
  check: {
    width: 16,
    height: 16,
    margin: 0,
    accentColor: S.fg,
    cursor: 'pointer',
    '&:disabled': { cursor: 'not-allowed', opacity: 0.35 },
    '&:focus-visible': A.focus
  },
  bulk: {
    position: 'fixed',
    left: '50%',
    bottom: 24,
    zIndex: 1500,
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    maxWidth: 'calc(100vw - 32px)',
    padding: '8px 8px 8px 16px',
    borderRadius: 12,
    background: S.fg,
    color: S.onFg,
    fontFamily: S.ui,
    fontSize: 14,
    boxShadow: '0 12px 32px rgba(23, 26, 32, 0.28)',
    animation: '$gxBulkIn 160ms ease-out'
  },
  '@keyframes gxBulkIn': { from: { opacity: 0, transform: 'translate(-50%, 8px)' }, to: { opacity: 1, transform: 'translateX(-50%)' } },
  bulkCount: { fontWeight: 600, marginRight: 8, whiteSpace: 'nowrap' },
  bulkClear: {
    all: 'unset',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: 8,
    color: '#C9CCD1',
    cursor: 'pointer',
    '& .material-icons': { fontSize: 18 },
    '&:hover': { background: 'rgba(255,255,255,0.12)', color: S.onFg },
    '&:focus-visible': { outline: `2px solid ${S.onFg}`, outlineOffset: 1 },
    [DARK]: { color: S.onFg, '&:hover': { background: S.fgActive, color: S.onFg } }
  }
});

type TableProps = { label: string; children: React.ReactNode; testId?: string; className?: string };

/** A list of records: white panel, sticky column headings, quiet row lines. */
export const Table: React.FC<TableProps> = ({ label, children, testId, className }) => {
  const c = useStyles();
  return (
    <div className={[c.frame, className].filter(Boolean).join(' ')}>
      <table className={c.table} aria-label={label} data-testid={testId}>{children}</table>
    </div>
  );
};

const INTERACTIVE = 'a, button, input, label, select, textarea, [role="menu"], [role="menuitem"], [role="menuitemradio"], [data-no-row-click]';

/**
 * Props for a row that opens something when clicked anywhere. Clicks on
 * links, buttons and checkboxes inside it keep their own meaning, and
 * selecting text doesn't open it. Cmd/Ctrl-click opens a new tab.
 */
export function rowClick(open: (newTab: boolean) => void, selected = false) {
  return {
    onClick: (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(INTERACTIVE)) return;
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.toString().length > 0) return;
      open(e.metaKey || e.ctrlKey);
    },
    'data-selected': selected || undefined
  };
}

export function useRowClasses() {
  const c = useStyles();
  return { clickable: c.clickable, selected: c.selected };
}

type SkeletonProps = { rows: number; widths: Array<string | number> };

/** Placeholder rows while a list loads, shaped like the real ones. */
export const SkeletonRows: React.FC<SkeletonProps> = ({ rows, widths }) => {
  const c = useStyles();
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} aria-hidden data-skeleton>
          {widths.map((w, i) => (
            <td key={i} style={{ height: 36 }}>
              {w ? <div className={c.bar} style={{ width: w, opacity: 1 - r * (0.5 / Math.max(rows, 1)) }} /> : null}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

type CheckboxProps = {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
  title?: string;
  testId?: string;
};

export const Checkbox: React.FC<CheckboxProps> = ({ checked, indeterminate, disabled, onChange, label, title, testId }) => {
  const c = useStyles();
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate && !checked; }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={c.check}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
      title={title || label}
      data-testid={testId}
    />
  );
};

type BulkBarProps = { count: number; noun: string; onClear: () => void; children: React.ReactNode };

/** Floats at the bottom while rows are selected: how many, what you can do with them, and a way out. */
export const BulkBar: React.FC<BulkBarProps> = ({ count, noun, onClear, children }) => {
  const c = useStyles();
  if (count === 0) return null;
  return (
    <div className={c.bulk} role="region" aria-label="Selected rows" data-testid="bulk-bar">
      <span className={c.bulkCount} aria-live="polite">{count} {noun} selected</span>
      {children}
      <button type="button" className={c.bulkClear} onClick={onClear} aria-label="Clear selection" title="Clear selection">
        <i className="material-icons" aria-hidden>close</i>
      </button>
    </div>
  );
};
